import { useMutation, useQuery } from "@tanstack/react-query"
import instance from "../../instance"

export interface IFetchEmailFilters {
    page?: string
    page_size?: string
    search?: string
    is_approved?: boolean
}

interface FetchEmailResponse {
    page: number;
    page_size: number;
    total_emails: number;
    emails: Email[];
}

interface Email {
    sender_email: string;
    subject: string;
    ai_generated_reply: string;
    sent_at: null | string;
    message: string;
    id: number;
    is_approved: boolean;
    created_at: string;
}

export const useFetchEmail = (payload: IFetchEmailFilters) => {
    const fetchEmail = async (): Promise<FetchEmailResponse> => {
        const { data } = await instance.get(`/email/all?page=${payload.page}&page_size=${payload.page_size}&search=${payload.search}&is_approved=${payload.is_approved}`)
        return data
    }
    
    const query = useQuery({
        queryFn: fetchEmail,
        queryKey: ['documents', payload]
    })
    
    return query
}

interface ISendMailPayload {
    id: number,
    is_updated: boolean,
    new_reply: string,
    status: string
}

export const useUpdateEmailAndSend = () => {
    const updateEmailAndSend = async (payload: ISendMailPayload) => {
        const { data } = await instance.post(`/email/send-reply/${payload.id}`, payload)
        return data
    }

    const mutation = useMutation({
        mutationFn: updateEmailAndSend
    })
    
    return mutation
}

// Bulk send emails interface and hook
interface IBulkSendEmailsPayload {
    email_ids: number[];
}

interface BulkSendResponse {
    success: boolean;
    message: string;
    sent_count: number;
    failed_emails?: number[];
}

export const useBulkSendEmails = () => {
    const bulkSendEmails = async (payload: IBulkSendEmailsPayload): Promise<BulkSendResponse> => {
        const { data } = await instance.post('/email/bulk-send-reply', payload)
        return data
    }

    const mutation = useMutation({
        mutationFn: bulkSendEmails
    })
    
    return mutation
}

// Optional: Bulk update emails (if you need to update multiple emails at once)
interface IBulkUpdateEmailsPayload {
    email_ids: number[];
    is_approved?: boolean;
    new_reply?: string;
}

export const useBulkUpdateEmails = () => {
    const bulkUpdateEmails = async (payload: IBulkUpdateEmailsPayload) => {
        const { data } = await instance.post('/email/bulk-update', payload)
        return data
    }

    const mutation = useMutation({
        mutationFn: bulkUpdateEmails
    })
    
    return mutation
}

// Export the Email type for use in components
export type { Email }